import { useToast } from '@ui/toasts/useToastManager'
import Button from '@ui/buttons/Button'
import { Appointment } from '@ca/common/types'
import { ModalBottom } from '@ui/components/Modal'
import Checklist from '@ui/elements/Checklist'
import {
  ChecklistItemState,
  ChecklistItemStates,
} from '@ui/elements/Checklist/types'
import QueueNumber from '@screens/vision/prescription/eyetest/QueueNumber'
import * as Sentry from '@sentry/react-native'
import Typography from '@ui/typography'
import { trpc } from 'api'
import Analytics from 'hooks/analytics/Analytics'
import { useLogout } from 'hooks/useLogout'
import useQuickLogin from 'hooks/useQuickLogin'
import useSession, { useCustomer } from 'hooks/useSession'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import BirthDateNode from './BirthDateNode'
import { useBirthDateForm } from './useBirthDateForm'
import useSurveyForm from 'features/survey/useSurveyForm'
import Survey from 'features/survey/Survey'
import { PdForm, useResolvedPd } from '@order/PdSection'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import { colors } from '@ui/colors'
import { useOrderState } from '@order/hooks/useOrderState'
import { MeasurementLocation } from 'hooks/measurements/useMeasureSdk'
import { resolvePd } from '@order/hooks/state/orderState'

interface Props {
  open: boolean
  close: () => void
}

const getState = (prev: ChecklistItemState, current: ChecklistItemState) => {
  if (prev !== ChecklistItemStates.done) {
    return ChecklistItemStates.upcoming
  }
  return current
}

export const EyetestAppoitmentModal = ({ open, close }: Props) => {
  const { t } = useTranslation(['eyetest', 'ui', 'common', 'measurement'])
  const [appointment, setAppointment] = useState<Appointment | null>(null)

  const [isPdSaved, setIsPdSaved] = useState(false)
  const pd = useResolvedPd()
  const pdType = useOrderState((s) => s.order.pd.current)

  const logout = useLogout()
  const customer = useCustomer()
  const { saveCustomer } = useQuickLogin()
  const setInfo = useSession((s) => s.setInfo)
  const toast = useToast()

  const customerBirthdate = customer?.birth_date

  const signUp = trpc.customer.appointments.create.useMutation({
    onSuccess: (data) => {
      setAppointment(data)
      Analytics.logEvent('prescription_eyetest', {
        sequence: data.sequence,
        pd_type: pdType || 'none',
      })
      setInfo({ hasEyeTest: true })
    },
    onError: (res) => {
      Sentry.captureException(
        new Error('Failed to create eye test appointment'),
        {
          extra: {
            error: res,
            customerId: customer?.id,
            pdValue: pd,
            timestamp: new Date().toISOString(),
          },
          tags: {
            location: 'EyetestAppoitmentModal',
          },
        }
      )
      toast({
        title: t('toasts:error.default.title'),
        text: t('toasts:error.default.desc'),
        variant: 'error',
      })
    },
  })

  const birthDate = useBirthDateForm()
  const survey = useSurveyForm({ showAgainIfFilledBefore: true })

  const signUpMutate = signUp.mutate
  const onSignUp = useCallback(() => {
    const pd = resolvePd(useOrderState.getState().order.pd)
    if (!pd) {
      setIsPdSaved(false)
      return
    }
    signUpMutate(pd)
  }, [signUpMutate])

  const onLogout = useCallback(async () => {
    await saveCustomer(customer)
    logout()
    close()
  }, [customer, saveCustomer, logout, close])

  const pdState = isPdSaved
    ? ChecklistItemStates.done
    : ChecklistItemStates.todo

  const birthDateState = getState(
    pdState,
    customerBirthdate ? ChecklistItemStates.done : ChecklistItemStates.todo
  )

  const surveyState = getState(
    birthDateState,
    survey.isSubmitted || !survey.show
      ? ChecklistItemStates.done
      : ChecklistItemStates.todo
  )

  const signUpState = getState(
    surveyState,
    appointment ? ChecklistItemStates.done : ChecklistItemStates.todo
  )

  const appointmentState = getState(signUpState, ChecklistItemStates.todo)

  return (
    <ModalWide
      hideBottomSpace
      open={open}
      close={close}
      title={t('eyetest:modal.title')}
      desc={t('eyetest:modal.desc')}
      body={
        <Checklist className="px-16 pb-4 pt-4">
          <Checklist.Step
            state={pdState}
            title={t('eyetest:measurement.title')}
            desc={t('eyetest:measurement.desc')}
            node={
              <PdForm
                disabled={!open || pdState !== ChecklistItemStates.todo}
                prescription="latest"
                location={MeasurementLocation.eyetest}
              >
                <Button
                  disabled={!pd}
                  variant="outlined"
                  label={t('ui:next')}
                  onPress={() => setIsPdSaved(true)}
                />
              </PdForm>
            }
            // TODO: Re enable this once we fix PdForm state sync issues
            // nodeChecked={
            //   <View className="pt-8">
            //     <PdForm
            //       prescription="latest"
            //       disabled={!open || pdState !== ChecklistItemStates.done}
            //       location={MeasurementLocation.eyetest}
            //     />
            //   </View>
            // }
            // isExpandable={!!pd}
          />
          {birthDate.show && (
            <Checklist.Step
              state={birthDateState}
              title={t('eyetest:customerDetails.title')}
              desc={t('eyetest:customerDetails.desc')}
              node={<BirthDateNode {...birthDate} />}
              nodeChecked={<BirthDateNode {...birthDate} />}
              isExpandable={!!customerBirthdate}
            />
          )}
          {survey.show && (
            <Checklist.Step
              state={surveyState}
              title={t('eyetest:survey.title')}
              desc={t('eyetest:survey.desc')}
              node={<Survey {...survey} />}
              nodeChecked={<Survey {...survey} />}
              isExpandable
            />
          )}
          <Checklist.Step
            state={signUpState}
            title={t('eyetest:appointment.title')}
            desc={t('eyetest:appointment.desc')}
            isExpandable={false}
            node={
              <View className="w-max self-start">
                <Button
                  variant="outlined"
                  label={t('ui:bookAppointment')}
                  loading={signUp.isPending}
                  onPress={onSignUp}
                />
              </View>
            }
          />
          <Checklist.Step
            state={appointmentState}
            title={t('eyetest:queue.title')}
            desc={t('eyetest:queue.desc')}
            node={
              <View className="gap-8">
                {appointment && <QueueNumber appointment={appointment} />}
                <Typography variant="MD" className="color-light">
                  {t('eyetest:queue.info')}
                </Typography>
                <View className="w-max self-start">
                  <Button
                    variant="outlined"
                    label={t('ui:logout')}
                    onPress={onLogout}
                    iconEnd="logout"
                    className="border-red-600"
                    overrideColorFront={colors.red[600]}
                    colors={[colors.red0, colors.red10]}
                  />
                </View>
              </View>
            }
            defaultOpen={!!appointment}
            staticBody={true}
            isExpandable={false}
          />
        </Checklist>
      }
    />
  )
}

export const EyetestInfoModal = ({ open, close }: Props) => {
  const { t } = useTranslation(['modals', 'ui'])
  const text = 'modals:eyetest'

  const logout = useLogout()
  const customer = useCustomer()
  const { saveCustomer } = useQuickLogin()

  const onLogout = useCallback(() => {
    saveCustomer(customer)
    logout()
    close()
  }, [customer, saveCustomer, logout, close])

  return (
    <>
      <ModalBottom
        hideBottomSpace
        open={open}
        close={close}
        title={t(`${text}.title`)}
        desc={t(`${text}.unused`)}
        buttons={
          <Button
            variant="outlined"
            label={t('ui:logout')}
            onPress={onLogout}
            iconEnd="logout"
            overrideColorFront={colors.red[600]}
          />
        }
      />
    </>
  )
}

export default EyetestAppoitmentModal
// export default EyetestInfoModal
